import { useEffect, useState } from 'react';
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import './AboutDashboard.css';
import { DiveElement, TimelineItem } from '../../../../data_types';
import { fetchTimelineElements } from '../../../backend/timeline_elements';
import { fetchDiveElements } from '../../../backend/dive_elements';

const AboutDashboard = () => {
    const [newTimelineElement, setNewTimelineElement] = useState({
        type: '',
        date: '',
        title: '',
        subtitle: '',
        description: ''
    });
    const [deleteTimlineElementId, setDeleteTimelineElementId] = useState('');

    const timelineBaseUrl = process.env.REACT_APP_TIMELINE_ELEMENTS_BASE_URL; // Base URL for the API server

    const [newHeadline, setNewHeadline] = useState({
        headline: ''
    });

    const [newDiveElement, setNewDiveElement] = useState({
        title: '',
        description: ''
    });
    const [deleteDiveElementId, setDeleteDiveElementId] = useState('');

    const deepDiveBaseUrl = process.env.REACT_APP_DEEP_DIVE_BASE_URL; // Base URL for the API server

    const [timelineData, setTimelineData] = useState<{ timeline_elements: TimelineItem[] }>({ timeline_elements: [] });
    const [diveData, setDiveData] = useState<{ dive_elements: DiveElement[] }>({ dive_elements: [] });

    useEffect(() => {
        const loadTimelineElements = async () => {
            const fetchedData = await fetchTimelineElements();
            setTimelineData({ timeline_elements: fetchedData });
        };

        const loadDiveElements = async () => {
            const fetchedData = await fetchDiveElements();
            setDiveData({ dive_elements: fetchedData.elements });
        };
        loadTimelineElements();
        loadDiveElements();
    }, []);

    const handleAddTimelineElementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewTimelineElement({ ...newTimelineElement, [name]: value });
    };

    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setNewTimelineElement({ ...newTimelineElement, [name as string]: value as string });
    };

    const handleAddTimelineElementSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { type, date, title, subtitle, description } = newTimelineElement;
        if (!type || !date || !title || !subtitle || !description) {
            alert('Please provide all fields for the timeline element.');
            return;
        }

        try {
            const response = await fetch(`${timelineBaseUrl}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newTimelineElement),
                credentials: 'include',
            });

            if (response.ok) {
                alert('Timeline element added successfully');
                setNewTimelineElement({ type: '', date: '', title: '', subtitle: '', description: '' });
            } else {
                alert('Failed to add timeline element');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error adding timeline element');
        }
    };

    const handleDeleteTimelineElementSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!deleteTimlineElementId) {
            alert('Please provide the ID of the timeline element to delete.');
            return;
        }

        try {
            const response = await fetch(`${timelineBaseUrl}/${deleteTimlineElementId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                alert('Timeline element deleted successfully');
                setDeleteTimelineElementId('');
            } else {
                alert('Failed to delete timeline element');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting timeline element');
        }
    };

    const handleChangeDiveHeadline = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { headline } = newHeadline;

        if (!headline) {
            alert('Please provide a headline for the deep dive');
            return;
        }

        try {
            const response = await fetch(`${deepDiveBaseUrl}/headline`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newHeadline),
                credentials: 'include',
            });

            if (response.ok) {
                alert('Deep dive headline changed successfully');
                setNewHeadline({ headline: '' });
            } else {
                alert('Failed to change deep dive headline');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error changing deep dive headline');
        }
    }

    const handleAddDiveElementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewDiveElement({ ...newDiveElement, [name]: value });
    };

    const handleAddDiveElementSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { title, description } = newDiveElement;
        if (!title || !description) {
            alert('Please provide all fields for the dive element.');
            return;
        }

        try {
            const response = await fetch(`${deepDiveBaseUrl}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newDiveElement),
                credentials: 'include',
            });

            if (response.ok) {
                alert('Dive element added successfully');
                setNewDiveElement({ title: '', description: '' });
            } else {
                alert('Failed to add dive element');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error adding dive element');
        }
    };

    const handleDeleteDiveElementSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!deleteDiveElementId) {
            alert('Please provide the ID of the dive element to delete.');
            return;
        }

        try {
            const response = await fetch(`${deepDiveBaseUrl}/${deleteDiveElementId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.ok) {
                alert('Dive element deleted successfully');
                setDeleteDiveElementId('');
            } else {
                alert('Failed to delete dive element');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error deleting timeline element');
        }
    };

    return (
        <Box className="about-dashboard">
            <form className='form-class' onSubmit={handleAddTimelineElementSubmit}>
                <h2>Add a New Timeline Element</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={newTimelineElement.type}
                        onChange={handleSelectChange}
                    >
                        <MenuItem value="manufacturing">Manufacturing</MenuItem>
                        <MenuItem value="warehouse">Warehouse</MenuItem>
                        <MenuItem value="business">Business</MenuItem>
                        <MenuItem value="award">Award</MenuItem>
                        <MenuItem value="thermostat">Thermostat</MenuItem>
                        <MenuItem value="airplane">Airplane</MenuItem>
                        <MenuItem value="expand">Expand</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="Date"
                    name="date"
                    value={newTimelineElement.date}
                    onChange={handleAddTimelineElementChange}
                    fullWidth
                    required
                />
                <TextField
                    label="Title"
                    name="title"
                    value={newTimelineElement.title}
                    onChange={handleAddTimelineElementChange}
                    fullWidth
                    required
                />
                <TextField
                    label="Subtitle"
                    name="subtitle"
                    value={newTimelineElement.subtitle}
                    onChange={handleAddTimelineElementChange}
                    fullWidth
                    required
                />
                <TextField
                    label="Description"
                    name="description"
                    value={newTimelineElement.description}
                    onChange={handleAddTimelineElementChange}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Add Timeline Element
                </Button>
            </form>

            <form onSubmit={handleDeleteTimelineElementSubmit}>
                <h2>Delete a Timeline Element</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={deleteTimlineElementId}
                        onChange={(e) => setDeleteTimelineElementId(e.target.value)}
                    >
                        {timelineData?.timeline_elements?.length > 0 ? (
                            timelineData?.timeline_elements.map((timeline_element) => (
                                <MenuItem value={timeline_element.displayId}>{timeline_element.title}</MenuItem>
                            ))
                        ) : (
                            <p>No timeline elements available</p>
                        )}
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" color="secondary">
                    Delete Timeline Element
                </Button>
            </form>

            <form className="form-class" onSubmit={handleChangeDiveHeadline}>
                <h2>Change Deep Dive Headline</h2>
                <TextField
                    label="Headline"
                    name="headline"
                    value={newHeadline.headline}
                    onChange={(e) => setNewHeadline({ headline: e.target.value })}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Change Deep Dive Headline
                </Button>
            </form>

            <form className='form-class' onSubmit={handleAddDiveElementSubmit}>
                <h2>Add a New Dive Element</h2>
                <TextField
                    label="Title"
                    name="title"
                    value={newDiveElement.title}
                    onChange={handleAddDiveElementChange}
                    fullWidth
                    required
                />
                <TextField
                    label="Description"
                    name="description"
                    value={newDiveElement.description}
                    onChange={handleAddDiveElementChange}
                    fullWidth
                    required
                />
                <Button type="submit" variant="contained" color="primary">
                    Add Dive Element
                </Button>
            </form>

            <form onSubmit={handleDeleteDiveElementSubmit}>
                <h2>Delete a Dive Element</h2>
                <FormControl fullWidth required>
                    <InputLabel id="type-label">Type</InputLabel>
                    <Select
                        labelId="type-label"
                        name="type"
                        value={deleteDiveElementId}
                        onChange={(e) => setDeleteDiveElementId(e.target.value)}
                    >
                        {diveData?.dive_elements?.length > 0 ? (
                            diveData?.dive_elements.map((dive_element) => (
                                <MenuItem value={dive_element.id}>{dive_element.title}</MenuItem>
                            ))
                        ) : (
                            <p>No dive elements available</p>
                        )}
                    </Select>
                </FormControl>
                <Button type="submit" variant="contained" color="secondary">
                    Delete Dive Element
                </Button>
            </form>
        </Box>
    );
};

export default AboutDashboard;